/** asyncSVG

  Author: Marcello Cosentino
  Reference: https://css-tricks.com/ajaxing-svg-sprite/

  asyncSVG looks for link tags with rel="async-svg", load them asynchronously
  and inject them inside the body (hidden).
  This way single SVG icons can be used as showed in the example below:

    <head>
      ...
      <link href="/static/images/svg/spritemap.svg" rel="async-svg">
    </head>
    <body>
      ...
      <svg role="img" class="svg-icon svg-icon--phone" title="Phone icon">
        <use xlink:href="#phone"></use>
      </svg>
    </body>

  The advantages of this approach are:
  - svg sprite file will be cached
  - use href is not external and thus css properties can be inherited from the page

  Sicurezza (CWE-79, DOM XSS da AJAX): lo sprite non viene piu' iniettato come markup
  (innerHTML). Si accettano solo file .svg della stessa origine della pagina e il DOM della
  risposta viene ricostruito nodo per nodo con una whitelist di elementi e attributi
  geometrici/di presentazione: script, foreignObject, animazioni, handler on* e href
  non locali vengono scartati.
*/

import $ from 'jquery';
import svg4everybody from 'svg4everybody';

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

const ALLOWED_ELEMENTS = [
  'svg', 'symbol', 'defs', 'g', 'title', 'desc', 'use',
  'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
  'clipPath', 'mask', 'linearGradient', 'radialGradient', 'stop',
];

const ALLOWED_ATTRIBUTES = [
  'id', 'class', 'viewBox', 'preserveAspectRatio', 'transform', 'd', 'points',
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'width', 'height',
  'fill', 'fill-rule', 'fill-opacity', 'clip-rule', 'clip-path', 'mask', 'opacity',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit',
  'stroke-dasharray', 'stroke-dashoffset', 'stroke-opacity',
  'offset', 'stop-color', 'stop-opacity', 'gradientUnits', 'gradientTransform',
  'clipPathUnits', 'maskUnits', 'maskContentUnits', 'href',
];

// solo URL relativi o della stessa origine, con estensione .svg
function isTrustedSpriteUrl(url) {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin && /\.svg$/i.test(parsed.pathname);
  } catch (e) {
    return false;
  }
}

// copia profonda del nodo SVG tenendo solo elementi e attributi ammessi
function cloneSafe(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.nodeValue);
  }
  if (node.nodeType !== Node.ELEMENT_NODE
    || node.namespaceURI !== SVG_NS
    || ALLOWED_ELEMENTS.indexOf(node.localName) === -1) {
    return null;
  }
  const el = document.createElementNS(SVG_NS, node.localName);
  Array.prototype.forEach.call(node.attributes, (attr) => {
    const name = attr.localName;
    if (ALLOWED_ATTRIBUTES.indexOf(name) === -1) return;
    if (name === 'href') {
      // solo riferimenti interni allo sprite (#id)
      if (attr.value.charAt(0) !== '#') return;
      el.setAttributeNS(attr.namespaceURI === XLINK_NS ? XLINK_NS : null, attr.name, attr.value);
      return;
    }
    if (attr.namespaceURI !== null) return;
    el.setAttribute(name, attr.value);
  });
  Array.prototype.forEach.call(node.childNodes, (child) => {
    const safeChild = cloneSafe(child);
    if (safeChild) el.appendChild(safeChild);
  });
  return el;
}

export function asyncSVG() {
  $('link[rel="async-svg"]').each(function () {
    const svgUrl = $(this).attr('href');
    if (!isTrustedSpriteUrl(svgUrl)) return;
    $.ajax({ url: svgUrl, dataType: 'xml' }).done((data) => {
      const sprite = data && data.documentElement ? cloneSafe(data.documentElement) : null;
      if (!sprite || sprite.localName !== 'svg') return;
      const $asyncSvg = $('<div id="asyncSvg" aria-hidden="true" style="position:absolute;top:0;visibility:hidden;height:0;width:0;"></div>');
      $asyncSvg[0].appendChild(sprite);
      $('body').prepend($asyncSvg);
      // force support to SVG External Content for old browsers (eg: IE11)
      svg4everybody();
    });
  });
}
